import { TreeNodeNormal } from 'antd/lib/tree-select/interface';
import { SeasonTree, UUID } from '../../../../redux/models';

export type NodeKind = 'org' | 'season' | 'property' | 'area';

export type OrgLoadState = 'idle' | 'loading' | 'loaded' | 'error';

export interface OrgRoot {
  id: UUID;
  name: string;
}

export const nodeKey = (kind: NodeKind, ids: string[]): string => `${kind}:${ids.join('/')}`;

export const LABEL_SEPARATOR = ' / ';

const composeLabel = (parts: Array<string | undefined>): string => parts.filter(Boolean).join(LABEL_SEPARATOR);

// Returns the orgId for a root org key, or null for season/property/area keys
// and synthetic placeholder keys (`org:<id>/__loading__`, `org:<id>/__error__`).
// Centralizes the placeholder-key format so callers do not encode it inline.
export const parseOrgKey = (key: string): UUID | null => {
  const [kind, rest] = key.split(':');
  if (kind !== 'org' || !rest || rest.includes('/')) return null;
  return rest;
};

export const decodeNodeIds = (keys: string[] | undefined): string[] => {
  if (!keys?.length) return [];
  return keys.map(key => {
    const colonIndex = key.indexOf(':');
    if (colonIndex < 0) return key;
    return (
      key
        .slice(colonIndex + 1)
        .split('/')
        .pop() ?? key
    );
  });
};

export const filterSeasonsTreeByCrop = (seasonsTree: SeasonTree[], cropId?: UUID): SeasonTree[] => {
  if (!cropId) return seasonsTree;
  return seasonsTree.filter(tree => tree.season.cropId === cropId);
};

const toAreaNode = (
  orgId: UUID,
  orgName: string,
  seasonName: string,
  propertyName: string,
  seasonId: UUID,
  propertyId: UUID,
  area: SeasonTree['seasonProperties'][number]['seasonAreas'][number]
): TreeNodeNormal => ({
  value: nodeKey('area', [orgId, seasonId, propertyId, area.id]),
  key: nodeKey('area', [orgId, seasonId, propertyId, area.id]),
  title: area.area.name,
  label: composeLabel([orgName, seasonName, propertyName, area.area.name]),
  isLeaf: true
});

const toPropertyNode = (
  orgId: UUID,
  orgName: string,
  seasonName: string,
  seasonId: UUID,
  property: SeasonTree['seasonProperties'][number]
): TreeNodeNormal => ({
  value: nodeKey('property', [orgId, seasonId, property.id]),
  key: nodeKey('property', [orgId, seasonId, property.id]),
  title: property.property.name,
  label: composeLabel([orgName, seasonName, property.property.name]),
  children: property.seasonAreas.map(area => toAreaNode(orgId, orgName, seasonName, property.property.name, seasonId, property.id, area))
});

export const toSeasonNode =
  (orgId: UUID, orgName = '') =>
  (tree: SeasonTree): TreeNodeNormal => ({
    value: nodeKey('season', [orgId, tree.season.id]),
    key: nodeKey('season', [orgId, tree.season.id]),
    title: tree.season.name,
    label: composeLabel([orgName, tree.season.name]),
    children: tree.seasonProperties.map(property => toPropertyNode(orgId, orgName, tree.season.name, tree.season.id, property))
  });

// Start with disableCheckbox=true so the user cannot click an org checkbox before its seasons load.
// Once seasons resolve, setOrgLoaded flips it back to false so rc-tree-select can render the half-checked state.
export const toOrgRoot = (org: OrgRoot): TreeNodeNormal => ({
  value: nodeKey('org', [org.id]),
  key: nodeKey('org', [org.id]),
  title: org.name,
  label: org.name,
  selectable: false,
  disableCheckbox: true,
  children: undefined
});

export const mergeOrgChildren = (orgsTree: TreeNodeNormal[], orgId: UUID, children: TreeNodeNormal[]): TreeNodeNormal[] =>
  orgsTree.map(node => (node.key === nodeKey('org', [orgId]) ? { ...node, children } : node));

// Returns true when the key is a root org key (e.g. `org:<uuid>`) with no descendant segment.
// Synthetic placeholder keys (`org:<id>/__loading__`, `org:<id>/__error__`) are not considered root org keys.
const isRootOrgKey = (key: string): boolean => parseOrgKey(key) !== null;

// Strip any root org keys from the value array so org ids never reach form state.
// Defensive companion to `expandOrgKeysToSeasons` in case an org key survives expansion (e.g. org has no loaded children).
export const stripOrgKeys = (selectedKeys: string[] | undefined): string[] => {
  if (!selectedKeys?.length) return [];
  return selectedKeys.filter(key => !isRootOrgKey(key));
};

// With `showCheckedStrategy='SHOW_PARENT'`, rc-tree-select collapses a fully-selected org into a single `org:<id>` key.
// We can't persist that synthetic id, so we expand it back into the org's currently-loaded season keys before stripping.
export const expandOrgKeysToSeasons = (selectedKeys: string[] | undefined, orgsTree: TreeNodeNormal[]): string[] => {
  if (!selectedKeys?.length) return [];
  const result: string[] = [];
  selectedKeys.forEach(key => {
    if (!isRootOrgKey(key)) {
      result.push(key);
      return;
    }
    const orgNode = orgsTree.find(node => node.key === key);
    if (!orgNode?.children) return;
    orgNode.children.forEach(child => {
      if (typeof child.key === 'string' && child.key.startsWith('season:')) result.push(child.key);
    });
  });
  return result;
};

export const dropOrphanedSelections = (selectedKeys: string[] | undefined, visibleKeys: Set<string>): string[] => {
  if (!selectedKeys?.length) return [];
  return selectedKeys.filter(key => visibleKeys.has(key));
};

export const collectVisibleKeys = (orgsTree: TreeNodeNormal[]): Set<string> => {
  const keys = new Set<string>();
  const walk = (nodes: TreeNodeNormal[] | undefined) => {
    if (!nodes) return;
    nodes.forEach(node => {
      keys.add(node.key);
      walk(node.children);
    });
  };
  walk(orgsTree);
  return keys;
};

const collectIdsForTree = (orgId: UUID, tree: SeasonTree): Array<[string, UUID]> => {
  const entries: Array<[string, UUID]> = [[tree.season.id, orgId]];
  tree.seasonProperties.forEach(property => {
    entries.push([property.id, orgId]);
    property.seasonAreas.forEach(area => entries.push([area.id, orgId]));
  });
  return entries;
};

export const buildSeasonIdToOrgMap = (seasonsByOrg: Record<UUID, SeasonTree[]>): Record<UUID, UUID> => {
  const allEntries: Array<[string, UUID]> = [];
  Object.entries(seasonsByOrg).forEach(([orgId, trees]) => {
    trees.forEach(tree => allEntries.push(...collectIdsForTree(orgId, tree)));
  });
  return Object.fromEntries(allEntries);
};

const encodePropertyKeys = (
  orgId: UUID,
  seasonId: UUID,
  property: SeasonTree['seasonProperties'][number],
  savedIds: string[]
): string[] => {
  const keys: string[] = [];
  if (savedIds.includes(property.id)) keys.push(nodeKey('property', [orgId, seasonId, property.id]));
  property.seasonAreas.forEach(area => {
    if (savedIds.includes(area.id)) keys.push(nodeKey('area', [orgId, seasonId, property.id, area.id]));
  });
  return keys;
};

const encodeTreeKeys = (orgId: UUID, tree: SeasonTree, savedIds: string[]): string[] => {
  const keys: string[] = [];
  if (savedIds.includes(tree.season.id)) keys.push(nodeKey('season', [orgId, tree.season.id]));
  tree.seasonProperties.forEach(property => keys.push(...encodePropertyKeys(orgId, tree.season.id, property, savedIds)));
  return keys;
};

export const encodeSavedSeasonIds = (savedIds: string[] | undefined, seasonsByOrg: Record<UUID, SeasonTree[]>): string[] => {
  if (!savedIds?.length) return [];
  const encoded: string[] = [];
  Object.entries(seasonsByOrg).forEach(([orgId, trees]) => {
    trees.forEach(tree => encoded.push(...encodeTreeKeys(orgId, tree, savedIds)));
  });
  return encoded;
};
